import { differenceInMilliseconds } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { logger } from "../../../common/logger";
import type { ContentfulArticle } from "../../../common/types/cmsTypes";
import { isDataAvailable, isDefaultData, isWaitingForData } from "../../../common/utils/asyncDataUtils";
import contentStore from "../../../stores/cms/contentStore";
import { ADVENT_CALENDAR_SLUG } from "./adventCalendarConstants";
import { getAdventCalendarCampaign } from "./adventCalendarUtils";

export const useAdventCalendarCampaign = () => {
   const [now, setNow] = useState(Date.now);
   const urlData = contentStore.urlData.data.find((data) => data.slug === ADVENT_CALENDAR_SLUG && data.type === "v2_article");
   const content = urlData ? contentStore.fetchedContent.get(urlData.id) : undefined;
   const article = content && isDataAvailable(content.entry) ? (content.entry.data as ContentfulArticle) : undefined;
   const articleId = urlData?.id;

   useEffect(() => {
      if (articleId) {
         contentStore.fetchContentIfNotAlreadyInStore(articleId, "v2_article");
      }
   }, [articleId]);

   const result = useMemo(() => {
      if (!article) {
         return {};
      }
      try {
         return { campaign: getAdventCalendarCampaign(article) };
      } catch (error) {
         return { error: error instanceof Error ? error.message : "Invalid calendar content." };
      }
   }, [article]);

   const campaign = result.campaign;
   const timeUntilOpening = campaign ? differenceInMilliseconds(campaign.opensAt, now) : 0;
   useEffect(() => {
      if (timeUntilOpening <= 0) {
         return;
      }
      const timer = setTimeout(() => setNow(Date.now()), Math.min(timeUntilOpening, 2147483647));
      return () => clearTimeout(timer);
   }, [timeUntilOpening]);

   const isLoading =
      isDefaultData(contentStore.urlData) ||
      isWaitingForData(contentStore.urlData) ||
      (!!content && (isDefaultData(content.entry) || isWaitingForData(content.entry)));
   const error = result.error || (!isLoading && !campaign ? "The calendar article could not be loaded." : undefined);
   const errorTrackingText = result.error
      ? "invalid_campaign_config"
      : isDataAvailable(contentStore.urlData) && !urlData
        ? "content_not_found_error"
        : "loading_error";

   useEffect(() => {
      if (error) {
         logger.error("contentful", "Invalid advent calendar configuration", error);
      }
   }, [error]);

   return {
      campaign,
      articleId,
      isLoading,
      error,
      errorTrackingText,
      isBeforeOpening: timeUntilOpening > 0
   };
};
